/**
 * Componente Select base
 */

import { forwardRef, SelectHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: boolean | string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, children, id, ...props }, ref) => {
    const selectId = id || props.name;
    const errorId = error ? `${selectId}-error` : undefined;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          aria-invalid={!!error}
          aria-describedby={errorId}
          className={cn(
            "flex h-11 w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2.5 text-sm",
            "transition-all duration-200 ease-out",
            "focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500",
            "hover:border-gray-300",
            "disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-gray-100",
            error &&
              "border-red-400 focus:ring-red-500/20 focus:border-red-500 bg-red-50/50",
            className,
          )}
          {...props}
        >
          {children}
        </select>
        {typeof error === "string" && error && (
          <p id={errorId} className="mt-1 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = "Select";

export default Select;
